import {
  CanvasTexture,
  ClampToEdgeWrapping,
  Color,
  LinearFilter,
  LinearMipmapLinearFilter,
  NoColorSpace,
  type Texture,
  type WebGLRenderer,
} from 'three'

import {PAW_ANISOTROPY, PAW_DARKEN, PAW_MASK_HALO_BLUR_RATIO, PAW_MASK_PX} from '../config'

/**
 * The paw print, drawn once into a canvas at start-up.
 *
 * WHY A TEXTURE AND NOT A SHADER. The ring and the sparks are written as
 * formulas because they are LIGHT: their picture really is one line of
 * `smoothstep`, and nobody is ever going to art-direct a shockwave. A paw print
 * is the first thing in this app whose SHAPE is campaign artwork, and the first
 * campaign that swaps the cat for a dog needs that shape changed by whoever
 * drew the dog. `PAW_PATH_D` is an SVG path, so that swap is a copy-paste out
 * of Illustrator or Figma with no GLSL and no redeploy of anyone's brain.
 *
 * It also filters better, which is the part that is easy to get backwards. A
 * floor decal is ALWAYS seen at a grazing angle. Mipmaps plus anisotropy are
 * built for exactly that; a shader's `fwidth` antialiasing gives a perfect
 * one-pixel edge head-on and then blows up along one axis as the surface tilts
 * away, which is precisely the viewing angle a phone held over a floor spends
 * all its time at.
 *
 * WHY IT IS GENERATED AND NOT SHIPPED. A 256x256 greyscale PNG of this is about
 * 3.6 KB - nothing against a 1.9 MB first load. Generating it anyway keeps the
 * effect at zero added assets, zero requests, zero new entries in
 * scripts/check-assets.mjs and zero new cache rules in public/_headers, and it
 * means there is no file that can 404 in the field. When a campaign does need
 * bespoke art, `FootprintTrail` takes a `Texture` it does not own, so pointing
 * it at a loaded PNG instead is a one-line change at the construction site.
 * That is the swap shockwave-ring.ts's header anticipated.
 *
 * WHY R AND G INSTEAD OF ALPHA. Everything here is drawn opaque onto an opaque
 * black field, so the canvas never contains a partial alpha. Canvas 2D is
 * premultiplied internally and three's default `premultiplyAlpha = false` asks
 * the browser to UN-premultiply on upload, which quantises the low-alpha end to
 * 8 bits and leaves a dark fringe along every antialiased edge. With no alpha
 * in the image that code path does not exist. The two coverages ride in R and G
 * instead, and cost nothing: the canvas was going to be RGBA either way.
 */

/**
 * A cat's front paw, in a 1000x1000 box with the toes toward the TOP.
 *
 * One metacarpal pad with two rear lobes, four digital pads, as seven separate
 * ellipses. Separate on purpose - a cat's toes do not touch its pad, and fusing
 * them (a smooth union, or one hand-drawn outline around the lot) turns the
 * print into a blob at exactly the on-screen size where it is most readable.
 *
 * Deliberately asymmetric: the outer toes are smaller and set further back than
 * the inner pair. That asymmetry is the ONLY thing that makes the left/right
 * mirroring in FootprintTrail visible, so a replacement path that is
 * symmetrical about x = 500 will silently turn the trail into one foot hopping.
 *
 * To replace: draw at 1000x1000 with the toes up, export SVG, paste the `d`
 * attribute. Keep every subpath wound the same way (the fill is non-zero) and
 * stay inside roughly 150..850 on both axes so the halo has room to sit.
 */
const PAW_PATH_D =
  // metacarpal pad
  'M 270 700 a 230 170 0 1 0 460 0 a 230 170 0 1 0 -460 0 Z ' +
  // rear lobes, left and right
  'M 275 830 a 85 75 0 1 0 170 0 a 85 75 0 1 0 -170 0 Z ' +
  'M 555 830 a 85 75 0 1 0 170 0 a 85 75 0 1 0 -170 0 Z ' +
  // digital pads, outer-left to outer-right. The two outer toes sit at
  // different heights (360 vs 298) - that 62-unit offset IS the handedness.
  'M 197 360 a 78 93 0 1 0 156 0 a 78 93 0 1 0 -156 0 Z ' +
  'M 312 205 a 88 103 0 1 0 176 0 a 88 103 0 1 0 -176 0 Z ' +
  'M 517 205 a 88 103 0 1 0 176 0 a 88 103 0 1 0 -176 0 Z ' +
  'M 652 298 a 78 93 0 1 0 156 0 a 78 93 0 1 0 -156 0 Z'

/** The box `PAW_PATH_D` is drawn in. */
const PAW_VIEW_BOX = 1000

/**
 * Draws the mask. `R` is the print's coverage, `G` is the dark halo's.
 *
 * Takes no three.js types so it can be reasoned about - and eyeballed in a
 * devtools console - as plain 2D drawing.
 *
 * Returns a blank canvas if a 2D context cannot be had. Callers treat an
 * all-zero mask as "no print", which is the same shape of graceful degradation
 * `createEntranceEffect` uses for `effect: 'none'`.
 */
export const createPawMask = (sizePx: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = sizePx
  canvas.height = sizePx

  const context = canvas.getContext('2d')
  if (context === null) return canvas

  // Opaque black first. This is what makes the alpha channel constant, which is
  // what keeps the un-premultiply path out of the upload - see the header.
  context.fillStyle = '#000000'
  context.fillRect(0, 0, sizePx, sizePx)

  const path = new Path2D(PAW_PATH_D)
  const scale = sizePx / PAW_VIEW_BOX

  // Pass 1: the halo into G. `shadowBlur` rather than `ctx.filter` - filter
  // arrived late in Safari, and this has to work on the iOS 16.4 floor the rest
  // of the app targets.
  //
  // Blur is in canvas pixels and is NOT scaled by the transform, so it is set
  // before the transform and computed from `sizePx` directly. Getting that
  // backwards makes the halo scale with the mask resolution instead of staying
  // a fixed fraction of the print.
  context.shadowColor = '#00ff00'
  context.shadowBlur = sizePx * PAW_MASK_HALO_BLUR_RATIO
  context.setTransform(scale, 0, 0, scale, 0, 0)
  context.fillStyle = '#00ff00'
  context.fill(path)

  // Pass 2: the body into R, added rather than drawn over, so the interior ends
  // up R=1 G=1 and the halo outside it R=0 G=coverage. The shader then reads the
  // halo as `g * (1 - r)`, which is why the green under the print is harmless.
  context.shadowColor = 'transparent'
  context.shadowBlur = 0
  context.globalCompositeOperation = 'lighter'
  context.fillStyle = '#ff0000'
  context.fill(path)

  return canvas
}

/**
 * The mask as a texture, ready to hand to the trail.
 *
 * ONE instance per session, and the trail does not dispose it - `app-module.ts`
 * owns it, the same way it owns the renderer the trail also borrows. Kept that
 * way even with a single borrower: the lifetime rule is what lets a second one
 * be added without revisiting who frees what.
 */
export const createPawTexture = (renderer: WebGLRenderer): Texture => {
  // Drawn BEFORE the texture is constructed. `CanvasTexture`'s constructor sets
  // `needsUpdate` itself, so building it around a blank canvas and painting
  // afterwards uploads the blank one and needs a second flag to fix.
  const texture = new CanvasTexture(createPawMask(PAW_MASK_PX))
  // Coverage, not colour. An sRGB transfer function here would bend the
  // coverage curve - the one case where the "canvas pixels are sRGB" rule that
  // model-color.ts follows does not apply, because these pixels are not pixels
  // of anything.
  texture.colorSpace = NoColorSpace
  texture.wrapS = ClampToEdgeWrapping
  texture.wrapT = ClampToEdgeWrapping
  texture.magFilter = LinearFilter
  // The reason this effect is a texture at all. A print on the floor is always
  // seen at a grazing angle and at a distance that changes as the user walks;
  // without mips it shimmers, which reads as the AR being fake.
  texture.minFilter = LinearMipmapLinearFilter
  texture.generateMipmaps = true
  texture.anisotropy = Math.min(PAW_ANISOTROPY, renderer.capabilities.getMaxAnisotropy())
  return texture
}

const BLACK = new Color(0, 0, 0)

/**
 * The accent, adjusted to how a print should read.
 *
 * Here, and not inside the trail, so anything else that draws a print on the
 * floor gets the same colour without copying the adjustment - a hue difference
 * between two prints is noticed long before it can be named.
 *
 * A clone: the caller hands the result to a uniform that holds it by reference.
 */
export const pawPrintColor = (accent: Color): Color => accent.clone().lerp(BLACK, PAW_DARKEN)
